import type { ApolloClient, MutationUpdaterFn } from '@apollo/client';
import { type Locale, sortPreferredLocations } from '@seek/ca-myprofile-utils';

import { getDataFromCache } from 'src/modules/apollo/apolloCache';
import * as Schema from 'src/types/graphql';

export const updateCacheUpdatePreferredLocations: MutationUpdaterFn<
  Schema.UpdatePreferredLocations2Mutation
> = (cache, { data }) => {
  if (data && 'viewer' in data.updatePreferredLocations2) {
    const _id = data?.updatePreferredLocations2?.viewer?._id || '';

    const preferredLocations2 =
      data?.updatePreferredLocations2?.viewer?.preferredLocations2 || [];

    const salaryPreferences2 =
      data?.updatePreferredLocations2?.viewer?.salaryPreferences2 || [];

    const rightsToWork =
      data?.updatePreferredLocations2?.viewer?.rightsToWork || [];

    cache.modify({
      id: cache.identify({
        __typename: 'Candidate',
        _id,
      }),
      fields: {
        preferredLocations2: () => preferredLocations2,
        salaryPreferences2: () => salaryPreferences2,
        rightsToWork: () => rightsToWork,
      },
    });
  }
};

export const generateUpdatePreferredLocationsOptimisticResponse =
  ({ _id, apolloClient }: { _id: string; apolloClient: ApolloClient<any> }) =>
  ({
    countryCode,
    languageCode,
    zone,
    isUnifiedSite,
    mutationMetadata: { newLocations: newPreferredLocations },
  }: Schema.UpdatePreferredLocations2MutationVariables &
    Record<string, any>) => {
    const salaryCacheData = getDataFromCache({
      cache: apolloClient.cache,
      query: Schema.GetSalaryPreferencesDocument,
      variables: { countryCode, languageCode },
    });
    const salaryPreferences2 =
      salaryCacheData?.viewer?.salaryPreferences2 || [];

    const rtwCacheData = getDataFromCache({
      cache: apolloClient.cache,
      query: Schema.GetRightsToWorkDocument,
      variables: { zone, languageCode, isUnifiedSite },
    });
    const rightsToWork = rtwCacheData?.viewer?.rightsToWork || [];

    const preferredLocations = sortPreferredLocations({
      preferredLocations: newPreferredLocations,
      countryCode: countryCode as Locale,
    });

    return {
      __typename: 'Mutation',
      updatePreferredLocations2: {
        __typename: 'UpdatePreferredLocations2Success',
        viewer: {
          __typename: 'Candidate',
          _id,
          preferredLocations2: preferredLocations,
          salaryPreferences2,
          rightsToWork,
        },
      },
    };
  };
